import type {
  BidPlacementReasonCode,
  BidPlacementResult,
} from '@/components/listings/shared/BulkBidModal/useBulkBidMutations'

import { isSupersededRow } from './types'
import type { BidSheetRowErrorCode, ValidatedBidSheetRow } from './types'

export type BadgeTone = 'success' | 'warning' | 'error'

export const ROW_ERROR_MESSAGE_IDS: Record<BidSheetRowErrorCode, string> = {
  missingListingId: 'Listings.uploadBidSheet.rowError.missingListingId',
  duplicate: 'Listings.uploadBidSheet.rowError.duplicate',
  notFound: 'Listings.uploadBidSheet.rowError.notFound',
  unacceptedTerms: 'Listings.uploadBidSheet.rowError.unacceptedTerms',
  makeAnOffer: 'Listings.uploadBidSheet.rowError.makeAnOffer',
  contract: 'Listings.uploadBidSheet.rowError.contract',
  noActiveAuction: 'Listings.uploadBidSheet.rowError.noActiveAuction',
  ended: 'Listings.uploadBidSheet.rowError.ended',
  alreadyWon: 'Listings.uploadBidSheet.rowError.alreadyWon',
  invalidAmount: 'Listings.uploadBidSheet.rowError.invalidAmount',
  belowMinimum: 'Listings.uploadBidSheet.rowError.belowMinimum',
  unreadable: 'Listings.uploadBidSheet.rowError.unreadable',
}

// 'alreadyWon' and 'noActiveAuction' intentionally point at the same locale
// keys as ROW_ERROR_MESSAGE_IDS above — a listing can be caught either at
// pre-upload validation or here at placement time (its status can change in
// between), and both should show the exact same buyer-facing string rather
// than maintaining two translations of the same fact.
const PLACEMENT_REASON_MESSAGE_IDS: Record<BidPlacementReasonCode, string> = {
  alreadyWon: ROW_ERROR_MESSAGE_IDS.alreadyWon,
  notBiddable: 'Listings.uploadBidSheet.rowError.notBiddable',
  noActiveAuction: ROW_ERROR_MESSAGE_IDS.noActiveAuction,
}

/**
 * Per-row status/note for the 'placed' step — shared by the live results
 * table and the After-Upload Report CSV (BP-2575), so the two can never
 * drift out of sync. `noteId` is a locale message id when the note is one
 * of our own controlled strings; `noteFallback` is plain, already-final
 * text (a raw backend error string, or one of useBulkBidMutations' own
 * hardcoded pre-filter messages) to show as-is when there's no locale id
 * for it. Exactly one of the two is meaningful for a given row — never both.
 */
type PlacedRowDisplay = {
  tone: BadgeTone
  statusId: string
  noteId: string | null
  noteFallback?: string
}

export const getPlacedRowDisplay = (
  row: ValidatedBidSheetRow,
  placement: BidPlacementResult | undefined
): PlacedRowDisplay => {
  // A superseded row's own error doesn't matter — its lot already placed
  // through a different row in the same group, which shares this same
  // listingId (and so the same `placement` lookup) — fall through to that
  // row's actual outcome instead of showing a spurious Error here.
  if (row.status === 'error' && !isSupersededRow(row)) {
    return {
      tone: 'error',
      statusId: 'Listings.uploadBidSheet.rowStatusError',
      noteId: ROW_ERROR_MESSAGE_IDS[row.errorCode],
    }
  }
  if (placement?.success && placement.winning) {
    return {
      tone: 'success',
      statusId: 'Listings.uploadBidSheet.rowStatusWinning',
      noteId: null,
    }
  }
  if (placement?.success) {
    return {
      tone: 'warning',
      statusId: 'Listings.uploadBidSheet.rowStatusLosing',
      noteId: 'Listings.uploadBidSheet.rowNoteOutbid',
    }
  }
  // useBulkBidMutations pre-filters an already-won listing into a failed
  // placement (success: false) but still marks it winning: true — without
  // this guard that falls through to the generic red Error case below,
  // which is alarming (and misleading) for a listing the buyer has already
  // won.
  if (placement?.winning) {
    return {
      tone: 'success',
      statusId: 'Listings.uploadBidSheet.rowStatusWinning',
      noteId: placement.reasonCode
        ? PLACEMENT_REASON_MESSAGE_IDS[placement.reasonCode]
        : null,
    }
  }
  // A reasonCode means useBulkBidMutations pre-filtered this listing itself
  // (never reached the batch API) — a controlled, localizable reason.
  // placement.errorMessage, when present instead, is the batch API's own
  // free-text failure and can't be localized; only fall back to a generic
  // locale-driven note when there's no placement result at all to explain
  // the failure.
  return {
    tone: 'error',
    statusId: 'Listings.uploadBidSheet.rowStatusError',
    noteId: placement?.reasonCode
      ? PLACEMENT_REASON_MESSAGE_IDS[placement.reasonCode]
      : placement?.errorMessage
        ? null
        : 'Listings.uploadBidSheet.rowNotePlacementError',
    noteFallback: placement?.reasonCode ? undefined : placement?.errorMessage,
  }
}
